import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Slider } from '@/components/ui/slider';
import {
  Plus, Loader2, Trash2, ArrowLeft, FileText, Shield, AlertTriangle,
  CheckCircle, Clock, Upload, Download, BarChart3, ChevronRight,
  Target, ClipboardList, Search, XCircle, Building2, Globe, Users,
  Radar, Cookie, Eye, RefreshCw, Calendar, Play, Zap
} from 'lucide-react';
import { toast } from 'sonner';
import {
  getPreAssessments, createPreAssessment, deletePreAssessment, getPreAssessment,
  getAssessmentQuestionnaire, submitAnswer, getAssessmentFindings, updateFinding,
  uploadEvidence, getAssessmentEvidence, deleteEvidence, seedPreAssessmentQuestions,
  runDomainScan, getAssessmentScans, deleteScan,
  createMonitoring, getMonitoring, deleteMonitoring, runMonitoringScan,
  downloadPreAssessmentReport,
} from '@/services/dpdpa';

const RISK_COLORS = { critical: 'bg-red-500', high: 'bg-orange-500', medium: 'bg-yellow-500', low: 'bg-emerald-500', not_assessed: 'bg-gray-400' };
const RISK_TEXT = { critical: 'text-red-500', high: 'text-orange-500', medium: 'text-yellow-500', low: 'text-emerald-500', not_assessed: 'text-gray-400' };
const SCORE_LABELS = { 0: 'Absent', 1: 'Ad hoc', 2: 'Defined', 3: 'Implemented', 4: 'Evidenced', 5: 'Optimized' };
const TEMPLATES = [
  { value: 'enterprise', label: 'Enterprise', desc: 'Full DPDPA assessment for large organizations' },
  { value: 'startup', label: 'Startup', desc: 'Focused assessment for growing companies' },
  { value: 'product', label: 'Product', desc: 'Product-specific data processing assessment' },
  { value: 'vendor', label: 'Vendor', desc: 'Vendor/processor compliance review' },
  { value: 'sdf', label: 'SDF-Focused', desc: 'Significant Data Fiduciary enhanced assessment' },
];

export default function PreAssessmentPage() {
  const [assessments, setAssessments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [showCreate, setShowCreate] = useState(false);

  const fetchAssessments = useCallback(async () => {
    try {
      setLoading(true);
      const res = await getPreAssessments();
      setAssessments(res.assessments || []);
    } catch { toast.error('Failed to load assessments'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAssessments(); }, [fetchAssessments]);

  if (selectedId) {
    return <AssessmentDetail id={selectedId} onBack={() => { setSelectedId(null); fetchAssessments(); }} />;
  }

  return (
    <div className="p-6 space-y-6" data-testid="pre-assessment-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold" data-testid="pre-assessment-title">DPDPA Pre-Assessment</h1>
          <p className="text-sm opacity-60">Evaluate your organization's DPDPA readiness through structured questionnaires and evidence</p>
        </div>
        <Button onClick={() => setShowCreate(true)} data-testid="create-assessment-btn">
          <Plus className="w-4 h-4 mr-2" /> New Assessment
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin opacity-50" /></div>
      ) : assessments.length === 0 ? (
        <Card><CardContent className="p-12 text-center">
          <ClipboardList className="w-12 h-12 mx-auto mb-3 opacity-20" />
          <h3 className="font-semibold mb-1">No assessments yet</h3>
          <p className="text-sm opacity-50 mb-4">Create your first DPDPA pre-assessment to evaluate compliance readiness</p>
          <Button onClick={() => setShowCreate(true)}><Plus className="w-4 h-4 mr-2" /> New Assessment</Button>
        </CardContent></Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {assessments.map(a => (
            <Card key={a.id} className="cursor-pointer hover:ring-1 hover:ring-primary/30 transition-all" onClick={() => setSelectedId(a.id)} data-testid={`assessment-card-${a.id}`}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-sm truncate">{a.name}</h3>
                    <p className="text-xs opacity-50 mt-0.5">{a.entity_name || 'No entity'} &middot; {a.template}</p>
                  </div>
                  <Badge variant={a.status === 'completed' ? 'default' : 'secondary'} className="text-[10px] shrink-0 ml-2">{a.status}</Badge>
                </div>
                <div className="flex items-center gap-3 mb-3">
                  <div className="flex-1">
                    <div className="flex items-center justify-between text-xs mb-1">
                      <span className="opacity-50">Readiness</span>
                      <span className="font-semibold">{a.overall_readiness || 0}%</span>
                    </div>
                    <div className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                      <div className={`h-full rounded-full transition-all ${a.overall_readiness >= 75 ? 'bg-emerald-500' : a.overall_readiness >= 50 ? 'bg-yellow-500' : a.overall_readiness >= 25 ? 'bg-orange-500' : 'bg-red-500'}`} style={{ width: `${a.overall_readiness || 0}%` }} />
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-3 text-xs opacity-50">
                  <span>{a.answered_questions || 0}/{a.total_questions || 0} answered</span>
                  {a.findings_count > 0 && <span className="text-red-400">{a.findings_count} findings</span>}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <CreateAssessmentDialog open={showCreate} onOpenChange={setShowCreate} onCreated={(a) => { setShowCreate(false); setSelectedId(a.id); fetchAssessments(); }} />
    </div>
  );
}

function CreateAssessmentDialog({ open, onOpenChange, onCreated }) {
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: '', template: 'enterprise', description: '', entity_name: '', industry: '',
    india_nexus: true, processes_child_data: false, processes_disability_data: false, is_sdf: false, outside_india_processing: false,
    employee_data: true, high_risk_processing: false, products: [], business_units: [],
  });
  const [productInput, setProductInput] = useState('');

  const handleCreate = async () => {
    if (!form.name) { toast.error('Assessment name required'); return; }
    try {
      setSaving(true);
      const res = await createPreAssessment(form);
      toast.success('Assessment created');
      onCreated(res);
    } catch { toast.error('Failed to create'); }
    finally { setSaving(false); }
  };

  const addProduct = () => {
    if (productInput.trim()) {
      setForm(p => ({ ...p, products: [...p.products, productInput.trim()] }));
      setProductInput('');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="create-assessment-dialog">
        <DialogHeader>
          <DialogTitle>New DPDPA Pre-Assessment</DialogTitle>
          <DialogDescription>Step {step} of 2: {step === 1 ? 'Scope & Entity' : 'Review & Create'}</DialogDescription>
        </DialogHeader>

        {step === 1 && (
          <div className="space-y-4">
            <div><Label>Assessment Name *</Label><Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="e.g. Q1 2026 Enterprise Readiness" data-testid="assessment-name-input" /></div>
            <div><Label>Template</Label>
              <Select value={form.template} onValueChange={v => setForm(p => ({ ...p, template: v }))}>
                <SelectTrigger data-testid="template-select"><SelectValue /></SelectTrigger>
                <SelectContent>{TEMPLATES.map(t => <SelectItem key={t.value} value={t.value}>{t.label} — {t.desc}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Entity Name</Label><Input value={form.entity_name} onChange={e => setForm(p => ({ ...p, entity_name: e.target.value }))} placeholder="Organization name" /></div>
              <div><Label>Industry</Label><Input value={form.industry} onChange={e => setForm(p => ({ ...p, industry: e.target.value }))} placeholder="e.g. Fintech, Healthcare" /></div>
            </div>
            <div><Label>Description</Label><Textarea value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} rows={2} placeholder="Assessment scope notes" /></div>
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase opacity-60">Applicability Screening</Label>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { key: 'india_nexus', label: 'India nexus (collect/process data in India)' },
                  { key: 'outside_india_processing', label: 'Processing outside India (goods/services to India)' },
                  { key: 'employee_data', label: 'Employee data processing' },
                  { key: 'processes_child_data', label: 'Processes children\'s data (under 18)' },
                  { key: 'processes_disability_data', label: 'Processes data of persons with disabilities (guardian-consent flows)' },
                  { key: 'is_sdf', label: 'Notified as Significant Data Fiduciary (SDF)' },
                  { key: 'high_risk_processing', label: 'High-risk or sensitive processing' },
                ].map(s => (
                  <div key={s.key} className="flex items-center gap-2 p-2 rounded border text-xs">
                    <Switch checked={form[s.key]} onCheckedChange={v => setForm(p => ({ ...p, [s.key]: v }))} />
                    <span>{s.label}</span>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <Label>Products / Channels</Label>
              <div className="flex gap-2 mt-1">
                <Input value={productInput} onChange={e => setProductInput(e.target.value)} placeholder="Add product" onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addProduct())} className="flex-1" />
                <Button variant="outline" size="sm" onClick={addProduct}>Add</Button>
              </div>
              <div className="flex flex-wrap gap-1 mt-1">
                {form.products.map((p, i) => (
                  <Badge key={i} variant="secondary" className="text-xs cursor-pointer" onClick={() => setForm(prev => ({ ...prev, products: prev.products.filter((_, j) => j !== i) }))}>{p} &times;</Badge>
                ))}
              </div>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-3">
            <div className="rounded-lg border p-4 space-y-2 text-sm">
              <div className="flex justify-between"><span className="opacity-50">Name</span><span className="font-medium">{form.name}</span></div>
              <div className="flex justify-between"><span className="opacity-50">Template</span><Badge variant="outline">{form.template}</Badge></div>
              <div className="flex justify-between"><span className="opacity-50">Entity</span><span>{form.entity_name || '-'}</span></div>
              <div className="flex justify-between"><span className="opacity-50">Industry</span><span>{form.industry || '-'}</span></div>
              <div className="flex justify-between"><span className="opacity-50">India nexus</span><Badge variant={form.india_nexus ? 'default' : 'secondary'}>{form.india_nexus ? 'Yes' : 'No'}</Badge></div>
              <div className="flex justify-between"><span className="opacity-50">Child data</span><Badge variant={form.processes_child_data ? 'destructive' : 'secondary'}>{form.processes_child_data ? 'Yes' : 'No'}</Badge></div>
              <div className="flex justify-between"><span className="opacity-50">Cross-border</span><Badge variant={form.outside_india_processing ? 'default' : 'secondary'}>{form.outside_india_processing ? 'Yes' : 'No'}</Badge></div>
              {form.products.length > 0 && <div className="flex justify-between"><span className="opacity-50">Products</span><span>{form.products.join(', ')}</span></div>}
            </div>
            <p className="text-xs opacity-50">The questionnaire will be auto-configured based on your scope. Conditional modules (Child Data, SDF) will only appear if applicable.</p>
          </div>
        )}

        <DialogFooter className="flex justify-between">
          {step > 1 && <Button variant="outline" onClick={() => setStep(step - 1)}>Back</Button>}
          <div className="flex gap-2 ml-auto">
            <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            {step < 2 ? (
              <Button onClick={() => setStep(2)} disabled={!form.name} data-testid="next-step-btn">Next</Button>
            ) : (
              <Button onClick={handleCreate} disabled={saving} data-testid="create-assessment-submit">
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Create Assessment
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AssessmentDetail({ id, onBack }) {
  const [assessment, setAssessment] = useState(null);
  const [tab, setTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [questionnaire, setQuestionnaire] = useState(null);
  const [findings, setFindings] = useState(null);
  const [evidence, setEvidence] = useState([]);
  const [scans, setScans] = useState([]);
  const [monitors, setMonitors] = useState([]);
  const [activeDomain, setActiveDomain] = useState(null);
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const a = await getPreAssessment(id);
      setAssessment(a);
    } catch { toast.error('Failed to load assessment'); }
    finally { setLoading(false); }
  }, [id]);

  const loadQuestionnaire = useCallback(async (domain) => {
    try {
      const res = await getAssessmentQuestionnaire(id, domain || undefined);
      setQuestionnaire(res);
    } catch { toast.error('Failed to load questionnaire'); }
  }, [id]);

  const loadFindings = useCallback(async () => {
    try {
      const res = await getAssessmentFindings(id);
      setFindings(res);
    } catch { toast.error('Failed to load findings'); }
  }, [id]);

  const loadEvidence = useCallback(async () => {
    try {
      const res = await getAssessmentEvidence(id);
      setEvidence(res.evidence || []);
    } catch { toast.error('Failed to load evidence'); }
  }, [id]);

  const loadScans = useCallback(async () => {
    try {
      const [sRes, mRes] = await Promise.all([getAssessmentScans(id), getMonitoring(id)]);
      setScans(sRes.scans || []);
      setMonitors(mRes.monitors || []);
    } catch { toast.error('Failed to load scans'); }
  }, [id]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (tab === 'questionnaire') loadQuestionnaire(activeDomain);
    if (tab === 'findings') loadFindings();
    if (tab === 'evidence') loadEvidence();
    if (tab === 'scans') loadScans();
  }, [tab, activeDomain, loadQuestionnaire, loadFindings, loadEvidence, loadScans]);

  const handleExportPDF = async () => {
    try {
      setExporting(true);
      const blob = await downloadPreAssessmentReport(id);
      const url = window.URL.createObjectURL(new Blob([blob]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `DPDPA_PreAssessment_${assessment?.name?.replace(/\s/g, '_') || 'Report'}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Report downloaded');
    } catch { toast.error('Export failed'); }
    finally { setExporting(false); }
  };

  if (loading || !assessment) return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin opacity-50" /></div>;

  return (
    <div className="p-6 space-y-4" data-testid="assessment-detail">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={onBack} data-testid="back-btn"><ArrowLeft className="w-4 h-4" /></Button>
        <div className="flex-1 min-w-0">
          <h1 className="text-xl font-bold truncate">{assessment.name}</h1>
          <p className="text-xs opacity-50">{assessment.entity_name} &middot; {assessment.template} &middot; {assessment.industry}</p>
        </div>
        <Button variant="outline" size="sm" onClick={handleExportPDF} disabled={exporting} data-testid="export-pdf-btn">
          {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />} Export PDF
        </Button>
        <Badge variant={assessment.status === 'completed' ? 'default' : 'secondary'}>{assessment.status}</Badge>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="assessment-tabs">
          <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="questionnaire" data-testid="tab-questionnaire">Questionnaire</TabsTrigger>
          <TabsTrigger value="scans" data-testid="tab-scans">Scans</TabsTrigger>
          <TabsTrigger value="findings" data-testid="tab-findings">Findings {findings?.stats?.total > 0 && <Badge variant="destructive" className="ml-1 text-[10px] px-1">{findings.stats.total}</Badge>}</TabsTrigger>
          <TabsTrigger value="evidence" data-testid="tab-evidence">Evidence</TabsTrigger>
        </TabsList>

        <TabsContent value="overview"><OverviewTab assessment={assessment} /></TabsContent>
        <TabsContent value="questionnaire">
          <QuestionnaireTab questionnaire={questionnaire} assessmentId={id} activeDomain={activeDomain} setActiveDomain={setActiveDomain} evidence={evidence} onAnswer={() => { load(); loadQuestionnaire(activeDomain); }} onEvidenceUploaded={loadEvidence} />
        </TabsContent>
        <TabsContent value="scans">
          <ScansTab scans={scans} monitors={monitors} assessmentId={id} onUpdate={() => { loadScans(); load(); loadQuestionnaire(activeDomain); }} />
        </TabsContent>
        <TabsContent value="findings"><FindingsTab findings={findings} assessmentId={id} onUpdate={loadFindings} /></TabsContent>
        <TabsContent value="evidence"><EvidenceTab evidence={evidence} assessmentId={id} questionnaire={questionnaire} onUpdate={loadEvidence} /></TabsContent>
      </Tabs>
    </div>
  );
}

function OverviewTab({ assessment }) {
  const ds = assessment.domain_scores || {};
  const DOMAIN_LABELS = {
    SCOPE: 'Scope & Applicability', LAWFUL_BASIS: 'Lawful Processing', NOTICE: 'Notice', CONSENT: 'Consent',
    RIGHTS: 'Data Principal Rights', SECURITY: 'Security Safeguards', BREACH: 'Breach Notification',
    RETENTION: 'Retention & Deletion', CHILD_DATA: 'Child & Vulnerable Data',
    GOVERNANCE: 'Governance & Processors', SDF: 'Significant Data Fiduciary', CROSS_BORDER: 'Cross-Border Transfer',
  };

  return (
    <div className="space-y-4 mt-4" data-testid="overview-tab">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card><CardContent className="p-4 text-center">
          <p className="text-3xl font-bold">{assessment.overall_readiness || 0}%</p>
          <p className="text-xs opacity-50">Overall Readiness</p>
        </CardContent></Card>
        <Card><CardContent className="p-4 text-center">
          <p className="text-3xl font-bold">{assessment.answered_questions || 0}<span className="text-base opacity-50">/{assessment.total_questions || 0}</span></p>
          <p className="text-xs opacity-50">Questions Answered</p>
        </CardContent></Card>
        <Card><CardContent className="p-4 text-center">
          <p className="text-3xl font-bold text-red-500">{assessment.findings_count || 0}</p>
          <p className="text-xs opacity-50">Open Findings</p>
        </CardContent></Card>
        <Card><CardContent className="p-4 text-center">
          <p className="text-3xl font-bold">{Object.keys(ds).filter(k => ds[k]?.answered > 0).length}<span className="text-base opacity-50">/{Object.keys(ds).length}</span></p>
          <p className="text-xs opacity-50">Domains Assessed</p>
        </CardContent></Card>
      </div>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-sm">Obligation Heatmap</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2" data-testid="heatmap">
            {Object.entries(DOMAIN_LABELS).map(([code, label]) => {
              const d = ds[code] || { readiness: 0, risk: 'not_assessed', answered: 0, total: 0 };
              return (
                <div key={code} className="rounded-lg border p-3 text-center" data-testid={`heatmap-${code}`}>
                  <div className={`w-3 h-3 rounded-full mx-auto mb-1 ${RISK_COLORS[d.risk]}`} />
                  <p className="text-xs font-medium">{label}</p>
                  <p className={`text-lg font-bold ${RISK_TEXT[d.risk]}`}>{d.readiness}%</p>
                  <p className="text-[10px] opacity-40">{d.answered}/{d.total} answered</p>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-sm">Scope Summary</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs">
            <div className="flex items-center gap-2"><Building2 className="w-3 h-3 opacity-50" /><span className="opacity-50">Entity:</span><span className="font-medium">{assessment.entity_name || '-'}</span></div>
            <div className="flex items-center gap-2"><Globe className="w-3 h-3 opacity-50" /><span className="opacity-50">India nexus:</span><Badge variant={assessment.india_nexus ? 'default' : 'secondary'} className="text-[10px]">{assessment.india_nexus ? 'Yes' : 'No'}</Badge></div>
            <div className="flex items-center gap-2"><Users className="w-3 h-3 opacity-50" /><span className="opacity-50">Child data:</span><Badge variant={assessment.processes_child_data ? 'destructive' : 'secondary'} className="text-[10px]">{assessment.processes_child_data ? 'Yes' : 'No'}</Badge></div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function QuestionnaireTab({ questionnaire, assessmentId, activeDomain, setActiveDomain, evidence = [], onAnswer, onEvidenceUploaded }) {
  const [saving, setSaving] = useState(null);
  const [localAnswers, setLocalAnswers] = useState({});
  const [pendingAnswers, setPendingAnswers] = useState({}); // queued saves
  const [uploadingFor, setUploadingFor] = useState(null); // questionId currently uploading evidence
  const inlineFileRefs = React.useRef({});

  const evidenceByQuestion = React.useMemo(() => {
    const map = {};
    (evidence || []).forEach(ev => {
      if (ev.question_id) {
        if (!map[ev.question_id]) map[ev.question_id] = [];
        map[ev.question_id].push(ev);
      }
    });
    return map;
  }, [evidence]);

  const persistAnswer = useCallback(async (questionId, answer, maturityScore) => {
    try {
      setSaving(questionId);
      await submitAnswer(assessmentId, { question_id: questionId, answer, maturity_score: maturityScore });
      setPendingAnswers(p => { const n = { ...p }; delete n[questionId]; return n; });
      onAnswer();
    } catch { toast.error('Failed to save'); }
    finally { setSaving(null); }
  }, [assessmentId, onAnswer]);

  const handleAnswer = (questionId, answer, maturityScore) => {
    setLocalAnswers(p => ({ ...p, [questionId]: { answer, maturity_score: maturityScore } }));
    setPendingAnswers(p => ({ ...p, [questionId]: { answer, maturity_score: maturityScore } }));
    persistAnswer(questionId, answer, maturityScore);
  };

  // Flush queued unsaved answers when the tab/window is hidden, navigated away, or unmounted
  React.useEffect(() => {
    const flush = async () => {
      const items = Object.entries(pendingAnswers);
      if (!items.length) return;
      await Promise.allSettled(
        items.map(([qid, v]) => submitAnswer(assessmentId, { question_id: qid, answer: v.answer, maturity_score: v.maturity_score }))
      );
      setPendingAnswers({});
    };
    const beforeUnload = (e) => {
      if (Object.keys(pendingAnswers).length > 0) {
        flush();
        e.preventDefault();
        e.returnValue = '';
      }
    };
    const visibilityChange = () => { if (document.visibilityState === 'hidden') flush(); };
    window.addEventListener('beforeunload', beforeUnload);
    document.addEventListener('visibilitychange', visibilityChange);
    return () => {
      window.removeEventListener('beforeunload', beforeUnload);
      document.removeEventListener('visibilitychange', visibilityChange);
      // Flush any unsaved on unmount (tab change inside the app)
      flush();
    };
  }, [pendingAnswers, assessmentId]);

  if (!questionnaire) return <div className="flex items-center justify-center h-32"><Loader2 className="w-6 h-6 animate-spin opacity-50" /></div>;
  const domains = questionnaire.domains || {};
  const domainKeys = Object.keys(domains);

  const handleQuestionEvidence = async (questionId, file) => {
    if (!file) return;
    try {
      setUploadingFor(questionId);
      await uploadEvidence(assessmentId, file, questionId, file.name);
      toast.success('Evidence attached to question');
      if (onEvidenceUploaded) onEvidenceUploaded();
    } catch {
      toast.error('Upload failed');
    } finally {
      setUploadingFor(null);
      const ref = inlineFileRefs.current[questionId];
      if (ref) ref.value = '';
    }
  };

  return (
    <div className="mt-4 space-y-4" data-testid="questionnaire-tab">
      <div className="flex flex-wrap items-center gap-2">
        <Button variant={!activeDomain ? 'default' : 'outline'} size="sm" onClick={() => setActiveDomain(null)}>All Domains</Button>
        {domainKeys.map(k => {
          const d = domains[k];
          return (
            <Button key={k} variant={activeDomain === k ? 'default' : 'outline'} size="sm" onClick={() => setActiveDomain(k)} data-testid={`domain-btn-${k}`}>
              {d.name} <Badge variant="secondary" className="ml-1 text-[10px]">{d.answered}/{d.total}</Badge>
            </Button>
          );
        })}
        {Object.keys(pendingAnswers).length > 0 && (
          <span className="ml-auto text-[11px] text-amber-600 flex items-center gap-1" data-testid="unsaved-indicator">
            <Loader2 className="w-3 h-3 animate-spin" />
            Saving {Object.keys(pendingAnswers).length} answer{Object.keys(pendingAnswers).length !== 1 ? 's' : ''}…
          </span>
        )}
      </div>

      {domainKeys.filter(k => !activeDomain || k === activeDomain).map(domainKey => {
        const d = domains[domainKey];
        return (
          <Card key={domainKey} data-testid={`domain-section-${domainKey}`}>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">{d.name} <Badge variant="outline" className="text-[10px]">{d.answered}/{d.total}</Badge></CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {Object.entries(d.sections || {}).map(([section, questions]) => (
                <div key={section}>
                  <h4 className="text-xs font-semibold uppercase opacity-50 mb-2">{section}</h4>
                  <div className="space-y-3">
                    {questions.map(q => {
                      const current = localAnswers[q.id] || q.answer;
                      const score = current?.maturity_score ?? current?.maturityScore ?? null;
                      const evList = evidenceByQuestion[q.id] || [];
                      const hasEvidence = evList.length > 0;
                      const evidenceMissing = q.evidence_required && !hasEvidence;
                      return (
                        <div key={q.id} className={`rounded-lg border p-3 ${evidenceMissing ? 'border-amber-300 bg-amber-50/40 dark:bg-amber-950/10' : ''}`} data-testid={`question-${q.id}`}>
                          <div className="flex items-start gap-2 mb-2">
                            <div className="flex-1">
                              <p className="text-sm">{q.text}</p>
                              {q.guidance && <p className="text-xs opacity-40 mt-1">{q.guidance}</p>}
                              <div className="flex flex-wrap gap-1 mt-1">
                                {q.is_mandatory && <Badge variant="destructive" className="text-[10px] px-1">Required</Badge>}
                                {q.evidence_required && (
                                  hasEvidence
                                    ? <Badge className="text-[10px] px-1 bg-emerald-500" data-testid={`evidence-uploaded-${q.id}`}><CheckCircle className="w-2.5 h-2.5 mr-0.5" /> Evidence ({evList.length})</Badge>
                                    : <Badge variant="outline" className="text-[10px] px-1 border-amber-400 text-amber-700">Evidence needed</Badge>
                                )}
                              </div>
                            </div>
                            {saving === q.id && <Loader2 className="w-4 h-4 animate-spin opacity-50 shrink-0" />}
                            {saving !== q.id && current && <CheckCircle className="w-4 h-4 text-emerald-500 shrink-0" />}
                          </div>

                          {q.question_type === 'binary' ? (
                            <div className="flex flex-wrap gap-1">
                              {(q.options || ['Yes', 'No']).map(opt => (
                                <Button key={opt} size="sm" variant={(current?.answer === opt) ? 'default' : 'outline'} className="text-xs h-7"
                                  onClick={() => handleAnswer(q.id, opt, opt === 'Yes' ? 4 : opt === 'Partially' ? 2 : 0)}>
                                  {opt}
                                </Button>
                              ))}
                            </div>
                          ) : (
                            <div className="flex items-center gap-2">
                              <Select
                                value={score !== null && score !== undefined ? String(score) : ''}
                                onValueChange={(v) => { const n = parseInt(v, 10); handleAnswer(q.id, SCORE_LABELS[n], n); }}
                              >
                                <SelectTrigger className="h-8 text-xs w-[260px]" data-testid={`maturity-select-${q.id}`}>
                                  <SelectValue placeholder="Select maturity..." />
                                </SelectTrigger>
                                <SelectContent>
                                  {[0, 1, 2, 3, 4, 5].map(n => (
                                    <SelectItem key={n} value={String(n)} data-testid={`maturity-option-${q.id}-${n}`}>
                                      <span className="font-mono text-[11px] mr-2 opacity-50">{n}</span>
                                      {SCORE_LABELS[n]}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          )}

                          {/* Inline evidence upload for evidence-required questions */}
                          {q.evidence_required && (
                            <div className="mt-3 pt-3 border-t border-dashed border-border/60 flex items-center gap-2 flex-wrap" data-testid={`evidence-row-${q.id}`}>
                              <input
                                ref={(el) => { if (el) inlineFileRefs.current[q.id] = el; }}
                                type="file"
                                className="hidden"
                                onChange={(e) => handleQuestionEvidence(q.id, e.target.files?.[0])}
                                data-testid={`evidence-input-${q.id}`}
                              />
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-7 text-xs"
                                onClick={() => inlineFileRefs.current[q.id]?.click()}
                                disabled={uploadingFor === q.id}
                                data-testid={`upload-question-evidence-${q.id}`}
                              >
                                {uploadingFor === q.id ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Upload className="w-3 h-3 mr-1" />}
                                {hasEvidence ? 'Add another' : 'Upload evidence'}
                              </Button>
                              {evList.map(ev => (
                                <Badge key={ev.id} className="text-[10px] bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100 cursor-default" title={ev.filename}>
                                  <CheckCircle className="w-2.5 h-2.5 mr-1" />
                                  {ev.filename.length > 28 ? ev.filename.slice(0, 25) + '…' : ev.filename}
                                </Badge>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}

function FindingsTab({ findings, assessmentId, onUpdate }) {
  const [updating, setUpdating] = useState(null);

  if (!findings) return <div className="flex items-center justify-center h-32"><Loader2 className="w-6 h-6 animate-spin opacity-50" /></div>;

  const { stats } = findings;
  const list = findings.findings || [];

  const handleUpdate = async (findingId, data) => {
    try {
      setUpdating(findingId);
      await updateFinding(assessmentId, findingId, data);
      toast.success('Finding updated');
      onUpdate();
    } catch { toast.error('Failed to update'); }
    finally { setUpdating(null); }
  };

  const DOMAIN_LABELS = {
    SCOPE: 'Scope & Applicability', LAWFUL_BASIS: 'Lawful Processing', NOTICE: 'Notice', CONSENT: 'Consent',
    RIGHTS: 'Data Principal Rights', SECURITY: 'Security Safeguards', BREACH: 'Breach Notification',
    RETENTION: 'Retention & Deletion', CHILD_DATA: 'Child & Vulnerable Data',
    GOVERNANCE: 'Governance & Processors', SDF: 'Significant Data Fiduciary', CROSS_BORDER: 'Cross-Border Transfer',
  };

  return (
    <div className="mt-4 space-y-4" data-testid="findings-tab">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <Card><CardContent className="p-3 text-center"><p className="text-xl font-bold text-red-500">{stats.critical}</p><p className="text-[10px] opacity-50">Critical</p></CardContent></Card>
        <Card><CardContent className="p-3 text-center"><p className="text-xl font-bold text-orange-500">{stats.high}</p><p className="text-[10px] opacity-50">High</p></CardContent></Card>
        <Card><CardContent className="p-3 text-center"><p className="text-xl font-bold text-yellow-500">{stats.medium}</p><p className="text-[10px] opacity-50">Medium</p></CardContent></Card>
        <Card><CardContent className="p-3 text-center"><p className="text-xl font-bold text-emerald-500">{stats.resolved}</p><p className="text-[10px] opacity-50">Resolved</p></CardContent></Card>
      </div>

      {list.length === 0 ? (
        <Card><CardContent className="p-8 text-center opacity-50">
          <CheckCircle className="w-8 h-8 mx-auto mb-2 opacity-30" />
          <p className="text-sm">No findings yet. Complete the questionnaire to generate findings.</p>
        </CardContent></Card>
      ) : (
        <div className="space-y-2">
          {list.map(f => (
            <Card key={f.id} data-testid={`finding-${f.id}`}>
              <CardContent className="p-3">
                <div className="flex items-start gap-3">
                  <div className={`w-2 h-2 rounded-full mt-1.5 shrink-0 ${RISK_COLORS[f.risk_level]}`} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm">{f.question_text}</p>
                    <div className="flex flex-wrap gap-1 mt-1">
                      <Badge variant="outline" className="text-[10px]">{DOMAIN_LABELS[f.domain] || f.domain}</Badge>
                      <Badge variant="outline" className="text-[10px]">Score: {f.current_score} &rarr; {f.target_score}</Badge>
                      <Badge className={`text-[10px] ${f.risk_level === 'critical' ? 'bg-red-500' : f.risk_level === 'high' ? 'bg-orange-500' : 'bg-yellow-500'}`}>{f.risk_level}</Badge>
                      {!f.evidence_present && f.evidence_required && <Badge variant="destructive" className="text-[10px]">Evidence missing</Badge>}
                      {f.source === 'domain_scan' && <Badge variant="secondary" className="text-[10px]">Scan</Badge>}
                    </div>
                    {f.remediation_notes && (
                      <div className="mt-2 p-2 rounded bg-blue-50 dark:bg-blue-950/30 text-xs text-blue-700 dark:text-blue-300">
                        <span className="font-semibold">Remediation: </span>{f.remediation_notes}
                      </div>
                    )}
                    {f.owner && <p className="text-xs opacity-40 mt-1">Owner: {f.owner}</p>}
                  </div>
                  <div className="flex gap-1">
                    {f.status !== 'in_progress' && f.status !== 'resolved' && (
                      <Button size="sm" variant="outline" className="text-xs h-7" onClick={() => handleUpdate(f.id, { status: 'in_progress' })}
                        disabled={updating === f.id}>In Progress</Button>
                    )}
                    {f.status !== 'resolved' && (
                      <Button size="sm" variant="outline" className="text-xs h-7 text-emerald-600" onClick={() => handleUpdate(f.id, { status: 'resolved' })}
                        disabled={updating === f.id}>Resolve</Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function EvidenceTab({ evidence, assessmentId, questionnaire, onUpdate }) {
  const [uploading, setUploading] = useState(false);
  const fileRef = React.useRef(null);
  const inlineRefs = React.useRef({});
  const [inlineUploading, setInlineUploading] = useState(null);

  // Build list of evidence-required questions from questionnaire
  const requiredQuestions = React.useMemo(() => {
    const list = [];
    const domains = questionnaire?.domains || {};
    Object.entries(domains).forEach(([domainKey, d]) => {
      Object.entries(d.sections || {}).forEach(([section, qs]) => {
        qs.forEach(q => {
          if (q.evidence_required) {
            list.push({ ...q, domainKey, domainName: d.name, section });
          }
        });
      });
    });
    return list;
  }, [questionnaire]);

  const evidenceByQuestion = React.useMemo(() => {
    const map = {};
    (evidence || []).forEach(ev => {
      if (ev.question_id) {
        if (!map[ev.question_id]) map[ev.question_id] = [];
        map[ev.question_id].push(ev);
      }
    });
    return map;
  }, [evidence]);

  const uploadedCount = requiredQuestions.filter(q => (evidenceByQuestion[q.id] || []).length > 0).length;
  const totalRequired = requiredQuestions.length;
  const requiredCoveragePct = totalRequired ? Math.round((uploadedCount / totalRequired) * 100) : 0;

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      setUploading(true);
      await uploadEvidence(assessmentId, file, '', file.name);
      toast.success('Evidence uploaded');
      onUpdate();
    } catch { toast.error('Upload failed'); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ''; }
  };

  const handleDelete = async (evidenceId) => {
    if (!window.confirm('Delete this evidence?')) return;
    try {
      await deleteEvidence(assessmentId, evidenceId);
      toast.success('Evidence deleted');
      onUpdate();
    } catch { toast.error('Delete failed'); }
  };

  const handleInlineUpload = async (questionId, file) => {
    if (!file) return;
    try {
      setInlineUploading(questionId);
      await uploadEvidence(assessmentId, file, questionId, file.name);
      toast.success('Evidence attached to question');
      onUpdate();
    } catch { toast.error('Upload failed'); }
    finally {
      setInlineUploading(null);
      const r = inlineRefs.current[questionId];
      if (r) r.value = '';
    }
  };

  return (
    <div className="mt-4 space-y-4" data-testid="evidence-tab">
      {/* Required-evidence coverage panel */}
      {totalRequired > 0 && (
        <Card data-testid="evidence-required-panel">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center justify-between">
              <span className="flex items-center gap-2">
                <Shield className="w-4 h-4" />
                Required Evidence Coverage
              </span>
              <Badge className={`text-[10px] ${requiredCoveragePct === 100 ? 'bg-emerald-500' : requiredCoveragePct >= 50 ? 'bg-amber-500' : 'bg-red-500'}`}>
                {uploadedCount}/{totalRequired} ({requiredCoveragePct}%)
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div
              className="w-full bg-muted h-2 rounded-full overflow-hidden mb-3"
              role="progressbar"
              aria-valuenow={requiredCoveragePct}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-label="Required Evidence Coverage"
              data-testid="evidence-coverage-bar"
            >
              <div
                className={`h-full transition-all ${requiredCoveragePct === 100 ? 'bg-emerald-500' : requiredCoveragePct >= 50 ? 'bg-amber-500' : 'bg-red-500'}`}
                style={{ width: `${requiredCoveragePct}%` }}
              />
            </div>
            <div className="space-y-1.5 max-h-[320px] overflow-y-auto">
              {requiredQuestions.map(q => {
                const evList = evidenceByQuestion[q.id] || [];
                const hasEvidence = evList.length > 0;
                return (
                  <div
                    key={q.id}
                    className={`flex items-center gap-2 p-2 rounded text-xs border ${hasEvidence ? 'bg-emerald-50 border-emerald-200 dark:bg-emerald-950/20 dark:border-emerald-900' : 'bg-amber-50 border-amber-200 dark:bg-amber-950/20 dark:border-amber-900'}`}
                    data-testid={`required-row-${q.id}`}
                  >
                    {hasEvidence
                      ? <CheckCircle className="w-4 h-4 text-emerald-500 shrink-0" />
                      : <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0" />}
                    <div className="flex-1 min-w-0">
                      <p className="truncate" title={q.text}>{q.text}</p>
                      <div className="flex items-center gap-1 mt-0.5">
                        <span className="text-[10px] opacity-50">{q.domainName} &middot; {q.section}</span>
                        {hasEvidence && <span className="text-[10px] text-emerald-600">&middot; {evList.length} file(s)</span>}
                      </div>
                    </div>
                    <input
                      ref={(el) => { if (el) inlineRefs.current[q.id] = el; }}
                      type="file"
                      className="hidden"
                      onChange={(e) => handleInlineUpload(q.id, e.target.files?.[0])}
                      data-testid={`required-input-${q.id}`}
                    />
                    <Button
                      size="sm"
                      variant={hasEvidence ? 'ghost' : 'outline'}
                      className="h-7 text-[11px] shrink-0"
                      onClick={() => inlineRefs.current[q.id]?.click()}
                      disabled={inlineUploading === q.id}
                      data-testid={`required-upload-${q.id}`}
                    >
                      {inlineUploading === q.id
                        ? <Loader2 className="w-3 h-3 animate-spin" />
                        : <><Upload className="w-3 h-3 mr-1" />{hasEvidence ? 'Add' : 'Upload'}</>}
                    </Button>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <p className="text-sm opacity-60">{evidence.length} evidence artifact{evidence.length !== 1 ? 's' : ''}</p>
          {evidence.some(e => e.content_type === 'positive_finding') && (
            <Badge className="text-[10px] bg-emerald-500">{evidence.filter(e => e.content_type === 'positive_finding').length} Pass</Badge>
          )}
          {evidence.some(e => e.content_type === 'negative_finding') && (
            <Badge variant="destructive" className="text-[10px]">{evidence.filter(e => e.content_type === 'negative_finding').length} Issues</Badge>
          )}
        </div>
        <div>
          <input ref={fileRef} type="file" className="hidden" onChange={handleUpload} data-testid="evidence-file-input" />
          <Button size="sm" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="upload-evidence-btn">
            {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />} Upload Evidence
          </Button>
        </div>
      </div>

      {evidence.length === 0 ? (
        <Card><CardContent className="p-8 text-center opacity-50">
          <FileText className="w-8 h-8 mx-auto mb-2 opacity-30" />
          <p className="text-sm">No evidence uploaded yet.</p>
        </CardContent></Card>
      ) : (
        <div className="space-y-2">
          {evidence.map(ev => (
            <Card key={ev.id} data-testid={`evidence-${ev.id}`}>
              <CardContent className="p-3 flex items-center gap-3">
                {ev.content_type === 'positive_finding' ? <CheckCircle className="w-5 h-5 text-emerald-500 shrink-0" /> :
                 ev.content_type === 'negative_finding' ? <XCircle className="w-5 h-5 text-red-500 shrink-0" /> :
                 ev.source === 'domain_scan' ? <Radar className="w-5 h-5 text-blue-400 shrink-0" /> :
                 <FileText className="w-5 h-5 opacity-40 shrink-0" />}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium truncate">{ev.filename}</p>
                    {ev.content_type === 'positive_finding' && <Badge className="text-[10px] bg-emerald-500 shrink-0">Pass</Badge>}
                    {ev.content_type === 'negative_finding' && <Badge variant="destructive" className="text-[10px] shrink-0">Issue</Badge>}
                    {ev.source === 'domain_scan' && ev.content_type === 'scan_result' && <Badge variant="secondary" className="text-[10px] shrink-0">Scan</Badge>}
                  </div>
                  {ev.description && <p className="text-xs opacity-50 mt-0.5 line-clamp-2">{ev.description}</p>}
                  <p className="text-xs opacity-30 mt-0.5">{ev.file_size > 0 ? `${(ev.file_size / 1024).toFixed(1)} KB` : ''} {ev.uploaded_by} &middot; {new Date(ev.created_at).toLocaleDateString()}</p>
                </div>
                {ev.file_url && <a href={ev.file_url} target="_blank" rel="noopener noreferrer"><Button variant="ghost" size="icon"><Download className="w-4 h-4" /></Button></a>}
                <Button variant="ghost" size="icon" onClick={() => handleDelete(ev.id)}><Trash2 className="w-4 h-4 text-red-400" /></Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function ScansTab({ scans, monitors, assessmentId, onUpdate }) {
  const [scanning, setScanning] = useState(false);
  const [domainInput, setDomainInput] = useState('');
  const [selectedScan, setSelectedScan] = useState(null);
  const [showMonitor, setShowMonitor] = useState(false);
  const [monitorForm, setMonitorForm] = useState({ domain: '', frequency: 'weekly' });
  const [runningMonitor, setRunningMonitor] = useState(null);

  const handleScan = async () => {
    if (!domainInput.trim()) { toast.error('Enter a domain'); return; }
    try {
      setScanning(true);
      const res = await runDomainScan(assessmentId, { domain: domainInput.trim(), max_pages: 10 });
      toast.success(`Scan complete: ${res.pages_scanned} pages, DPDPA score ${res.dpdpa_score}%`);
      setDomainInput('');
      onUpdate();
    } catch { toast.error('Scan failed'); }
    finally { setScanning(false); }
  };

  const handleAddMonitor = async () => {
    if (!monitorForm.domain.trim()) { toast.error('Enter a domain'); return; }
    try {
      await createMonitoring(assessmentId, monitorForm);
      toast.success('Monitoring scheduled');
      setShowMonitor(false);
      setMonitorForm({ domain: '', frequency: 'weekly' });
      onUpdate();
    } catch { toast.error('Failed to create monitor'); }
  };

  const handleRunMonitor = async (monitorId) => {
    try {
      setRunningMonitor(monitorId);
      await runMonitoringScan(assessmentId, monitorId);
      toast.success('Scan complete');
      onUpdate();
    } catch { toast.error('Scan failed'); }
    finally { setRunningMonitor(null); }
  };

  const [prefilling, setPrefilling] = useState(false);
  const [prefillResult, setPrefillResult] = useState(null);
  const handleSmartPrefill = async (overwrite = false) => {
    try {
      setPrefilling(true);
      const token = localStorage.getItem('token');
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(
        `${API_URL}/api/dpdpa/pre-assessment/assessments/${assessmentId}/smart-prefill?overwrite=${overwrite ? 'true' : 'false'}`,
        { method: 'POST', headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) {
        // Try JSON, then plain text — surface whatever the server actually returned
        const ctype = res.headers.get('content-type') || '';
        let detail = '';
        if (ctype.includes('application/json')) {
          const err = await res.json().catch(() => ({}));
          detail = err.detail || err.message || '';
        } else {
          detail = (await res.text().catch(() => '')).slice(0, 240);
        }
        throw new Error(detail || `Smart prefill failed (HTTP ${res.status})`);
      }
      const data = await res.json();
      setPrefillResult(data);
      const dom = data.by_domain || {};
      const breakdown = Object.entries(dom).map(([k, v]) => `${v} ${k}`).join(', ');
      toast.success(
        data.applied > 0
          ? `Smart Prefill: ${data.applied} answers added${breakdown ? ' — ' + breakdown : ''}${data.skipped_existing ? ` (${data.skipped_existing} already-answered skipped)` : ''}`
          : (data.skipped_existing > 0
              ? `All ${data.skipped_existing} matching questions already answered. Use "Re-run & overwrite" to refresh.`
              : 'No questions matched the scanner signals.')
      );
      onUpdate();
    } catch (e) {
      toast.error(e.message || 'Smart prefill failed');
    } finally { setPrefilling(false); }
  };

  const handleClearPrefill = async () => {
    if (!confirm('Remove all answers that were auto-filled by Smart Prefill?')) return;
    try {
      const token = localStorage.getItem('token');
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(
        `${API_URL}/api/dpdpa/pre-assessment/assessments/${assessmentId}/smart-prefill`,
        { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) throw new Error('Failed');
      const data = await res.json();
      toast.success(`Removed ${data.removed} prefilled answers`);
      setPrefillResult(null);
      onUpdate();
    } catch { toast.error('Failed to clear prefilled answers'); }
  };

  const handleDeleteMonitor = async (monitorId) => {
    if (!window.confirm('Remove this monitor?')) return;
    try {
      await deleteMonitoring(assessmentId, monitorId);
      toast.success('Monitor removed');
      onUpdate();
    } catch { toast.error('Failed'); }
  };

  const r = selectedScan?.results;

  return (
    <div className="mt-4 space-y-4" data-testid="scans-tab">
      {/* Scan Input */}
      <Card>
        <CardContent className="p-4">
          <div className="flex gap-2 items-end">
            <div className="flex-1">
              <Label className="text-xs">Scan Domain (privacy policy, cookies, consent banner)</Label>
              <Input value={domainInput} onChange={e => setDomainInput(e.target.value)} placeholder="e.g. example.com" onKeyDown={e => e.key === 'Enter' && handleScan()} data-testid="scan-domain-input" />
            </div>
            <Button onClick={handleScan} disabled={scanning} data-testid="run-scan-btn">
              {scanning ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Radar className="w-4 h-4 mr-2" />} Scan (10 pages)
            </Button>
            <Button variant="outline" onClick={() => setShowMonitor(true)} data-testid="add-monitor-btn">
              <Calendar className="w-4 h-4 mr-2" /> Schedule
            </Button>
          </div>

          {/* Smart Pre-fill panel */}
          {scans.length > 0 && (
            <div className="mt-4 pt-4 border-t flex flex-col md:flex-row md:items-center gap-3" data-testid="smart-prefill-panel">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold flex items-center gap-2 flex-wrap">
                  <Zap className="w-4 h-4 text-amber-500" />
                  Smart Pre-fill from latest scan
                  {prefillResult && (
                    <Badge
                      className="text-[10px] bg-emerald-50 text-emerald-700 border border-emerald-200 ml-1"
                      data-testid="prefill-result-summary"
                      title={prefillResult.message || ''}
                    >
                      {prefillResult.applied} applied
                      {prefillResult.skipped_existing ? ` · ${prefillResult.skipped_existing} skipped` : ''}
                      {prefillResult.scan_domain ? ` · ${prefillResult.scan_domain}` : ''}
                    </Badge>
                  )}
                </p>
                <p className="text-xs opacity-60 mt-0.5">
                  Auto-answers ~25–30 NOTICE / CONSENT / SECURITY / RIGHTS / RETENTION / BREACH questions using scanner findings — privacy policy presence, banner state, multilingual notice, withdrawal mechanism, dark patterns, encryption.
                </p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Button onClick={() => handleSmartPrefill(false)} disabled={prefilling} data-testid="smart-prefill-btn">
                  {prefilling ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Zap className="w-4 h-4 mr-2" />}
                  Smart Pre-fill
                </Button>
                <Button variant="outline" onClick={() => handleSmartPrefill(true)} disabled={prefilling} data-testid="smart-prefill-overwrite-btn" title="Re-run and overwrite existing answers">
                  <RefreshCw className="w-3 h-3" />
                </Button>
                {prefillResult && prefillResult.applied > 0 && (
                  <Button variant="ghost" size="sm" onClick={handleClearPrefill} data-testid="clear-prefill-btn" title="Remove all prefilled answers">
                    <Trash2 className="w-3 h-3 text-red-400" />
                  </Button>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Monitoring Schedules */}
      {monitors.length > 0 && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><RefreshCw className="w-4 h-4" /> Continuous Monitoring</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {monitors.map(m => (
              <div key={m.id} className="flex items-center gap-3 p-2 rounded border text-sm" data-testid={`monitor-${m.id}`}>
                <Globe className="w-4 h-4 opacity-50 shrink-0" />
                <div className="flex-1 min-w-0">
                  <span className="font-medium">{m.domain}</span>
                  <span className="text-xs opacity-50 ml-2">Every {m.frequency} &middot; {m.scan_count} scans</span>
                  {m.next_scan_at && <span className="text-xs opacity-40 ml-2">Next: {new Date(m.next_scan_at).toLocaleDateString()}</span>}
                </div>
                <Badge variant={m.active ? 'default' : 'secondary'} className="text-[10px]">{m.active ? 'Active' : 'Paused'}</Badge>
                <Button variant="ghost" size="sm" onClick={() => handleRunMonitor(m.id)} disabled={runningMonitor === m.id} data-testid={`run-monitor-${m.id}`}>
                  {runningMonitor === m.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Play className="w-3 h-3" />}
                </Button>
                <Button variant="ghost" size="icon" onClick={() => handleDeleteMonitor(m.id)}><Trash2 className="w-3 h-3 text-red-400" /></Button>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Scan Results */}
      {scans.length === 0 ? (
        <Card><CardContent className="p-8 text-center opacity-50">
          <Radar className="w-8 h-8 mx-auto mb-2 opacity-30" />
          <p className="text-sm">No scans yet. Enter a domain above to analyze privacy policy, cookies, and consent banner.</p>
        </CardContent></Card>
      ) : selectedScan ? (
        <div className="space-y-3">
          <Button variant="ghost" size="sm" onClick={() => setSelectedScan(null)}><ArrowLeft className="w-3 h-3 mr-1" /> Back to scan list</Button>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <Globe className="w-4 h-4" /> {r?.domain} — DPDPA Score: <span className={`font-bold ${r?.dpdpa_compliance?.score >= 70 ? 'text-emerald-500' : r?.dpdpa_compliance?.score >= 40 ? 'text-yellow-500' : 'text-red-500'}`}>{r?.dpdpa_compliance?.score}%</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Summary Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                <div className="rounded-lg border p-3 text-center"><p className="text-lg font-bold">{r?.pages_scanned}</p><p className="text-[10px] opacity-50">Pages Scanned</p></div>
                <div className="rounded-lg border p-3 text-center">
                  <p className={`text-lg font-bold ${r?.privacy_policy?.found ? 'text-emerald-500' : 'text-red-500'}`}>{r?.privacy_policy?.found ? 'Found' : 'Missing'}</p>
                  <p className="text-[10px] opacity-50">Privacy Policy</p>
                </div>
                <div className="rounded-lg border p-3 text-center"><p className="text-lg font-bold">{r?.cookies?.total || 0}</p><p className="text-[10px] opacity-50">Cookies</p></div>
                <div className="rounded-lg border p-3 text-center">
                  <p className={`text-lg font-bold ${r?.consent_banner?.found ? 'text-emerald-500' : 'text-red-500'}`}>{r?.consent_banner?.found ? 'Found' : 'Missing'}</p>
                  <p className="text-[10px] opacity-50">Consent Banner</p>
                </div>
              </div>

              {/* DPDPA Compliance Checks */}
              <div>
                <h4 className="text-xs font-semibold uppercase opacity-50 mb-2">DPDPA Compliance Checks</h4>
                <div className="space-y-1">
                  {(r?.dpdpa_compliance?.checks || []).map((c, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs p-1.5 rounded border">
                      {c.status === 'pass' ? <CheckCircle className="w-3.5 h-3.5 text-emerald-500 shrink-0" /> : c.status === 'fail' ? <XCircle className="w-3.5 h-3.5 text-red-500 shrink-0" /> : <AlertTriangle className="w-3.5 h-3.5 text-yellow-500 shrink-0" />}
                      <span className="flex-1">{c.check}</span>
                      {c.detail && <span className="text-[10px] opacity-40 truncate max-w-[200px]">{c.detail}</span>}
                    </div>
                  ))}
                </div>
              </div>

              {/* Cookie Analysis */}
              {r?.cookies?.total > 0 && (
                <div>
                  <h4 className="text-xs font-semibold uppercase opacity-50 mb-2">Cookie Analysis ({r.cookies.total} total)</h4>
                  <div className="flex flex-wrap gap-2 mb-2">
                    {Object.entries(r.cookies.categories || {}).filter(([, v]) => v > 0).map(([cat, count]) => (
                      <Badge key={cat} variant={cat === 'marketing' || cat === 'social' ? 'destructive' : cat === 'analytics' ? 'default' : 'outline'} className="text-xs capitalize">{cat}: {count}</Badge>
                    ))}
                  </div>
                  <div className="max-h-32 overflow-y-auto space-y-0.5">
                    {(r.cookies.details || []).map((c, i) => (
                      <div key={i} className="flex items-center gap-2 text-xs p-1 rounded bg-black/5 dark:bg-white/5">
                        <Cookie className="w-3 h-3 opacity-30 shrink-0" />
                        <span className="font-mono">{c.name}</span>
                        <Badge variant="outline" className="text-[10px] capitalize">{c.category}</Badge>
                        {c.js_set && <Badge variant="secondary" className="text-[10px]">JS</Badge>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Privacy Policy Analysis */}
              {r?.privacy_policy?.found && (
                <div>
                  <h4 className="text-xs font-semibold uppercase opacity-50 mb-2">Privacy Policy — {r.privacy_policy.completeness_score}% Complete</h4>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-1">
                    {Object.entries(r.privacy_policy.analysis || {}).map(([key, val]) => (
                      <div key={key} className="flex items-center gap-1 text-xs p-1">
                        {val ? <CheckCircle className="w-3 h-3 text-emerald-500 shrink-0" /> : <XCircle className="w-3 h-3 text-red-400 shrink-0" />}
                        <span className="capitalize">{key.replace(/_/g, ' ').replace('mentions ', '')}</span>
                      </div>
                    ))}
                  </div>
                  {r.privacy_policy.url && <a href={r.privacy_policy.url} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-500 hover:underline mt-1 inline-block">{r.privacy_policy.url}</a>}
                </div>
              )}

              {/* Issues */}
              {(r?.dpdpa_compliance?.issues || []).length > 0 && (
                <div>
                  <h4 className="text-xs font-semibold uppercase opacity-50 mb-2">Issues Found ({r.dpdpa_compliance.issues.length})</h4>
                  <div className="space-y-1">
                    {r.dpdpa_compliance.issues.map((issue, i) => (
                      <div key={i} className="flex flex-col gap-1 text-xs p-2 rounded border">
                        <div className="flex items-start gap-2">
                          <Badge className={`text-[10px] shrink-0 ${issue.severity === 'critical' ? 'bg-red-500' : issue.severity === 'high' ? 'bg-orange-500' : 'bg-yellow-500'}`}>{issue.severity}</Badge>
                          <span className="flex-1">{issue.issue}</span>
                          <Badge variant="outline" className="text-[10px]">{issue.obligation}</Badge>
                        </div>
                        {issue.remediation && (
                          <div className="ml-14 p-1.5 rounded bg-blue-50 dark:bg-blue-950/30 text-[11px] text-blue-700 dark:text-blue-300">
                            <span className="font-semibold">Fix: </span>{issue.remediation}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      ) : (
        <div className="space-y-2">
          {scans.map(s => (
            <Card key={s.id} className="cursor-pointer hover:ring-1 hover:ring-primary/30 transition-all" onClick={() => setSelectedScan(s)} data-testid={`scan-card-${s.id}`}>
              <CardContent className="p-3 flex items-center gap-3">
                <Radar className={`w-5 h-5 shrink-0 ${s.scan_status === 'completed' ? 'text-emerald-500' : 'text-red-400'}`} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium">{s.domain}</p>
                  <div className="flex gap-3 text-xs opacity-50 mt-0.5">
                    <span>{s.pages_scanned} pages</span>
                    <span>{s.cookies_found} cookies</span>
                    <span>Privacy: {s.privacy_policy_found ? 'Yes' : 'No'}</span>
                    <span>Banner: {s.consent_banner_found ? 'Yes' : 'No'}</span>
                    <span>{new Date(s.created_at).toLocaleString()}</span>
                  </div>
                </div>
                <Badge className={`text-xs ${s.dpdpa_score >= 70 ? 'bg-emerald-500' : s.dpdpa_score >= 40 ? 'bg-yellow-500' : 'bg-red-500'}`}>{s.dpdpa_score}%</Badge>
                <ChevronRight className="w-4 h-4 opacity-30" />
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Add Monitor Dialog */}
      <Dialog open={showMonitor} onOpenChange={setShowMonitor}>
        <DialogContent data-testid="add-monitor-dialog">
          <DialogHeader><DialogTitle>Schedule Continuous Monitoring</DialogTitle><DialogDescription>Automatically re-scan a domain on a schedule</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div><Label>Domain</Label><Input value={monitorForm.domain} onChange={e => setMonitorForm(p => ({ ...p, domain: e.target.value }))} placeholder="example.com" data-testid="monitor-domain-input" /></div>
            <div><Label>Frequency</Label>
              <Select value={monitorForm.frequency} onValueChange={v => setMonitorForm(p => ({ ...p, frequency: v }))}>
                <SelectTrigger data-testid="monitor-frequency-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="daily">Daily</SelectItem>
                  <SelectItem value="weekly">Weekly</SelectItem>
                  <SelectItem value="monthly">Monthly</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMonitor(false)}>Cancel</Button>
            <Button onClick={handleAddMonitor} data-testid="save-monitor-btn">Schedule Monitoring</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
